import React, { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Navbar, Footer, ContactBar, ScrollToTop, SEOHead } from './components';
import { useTheme } from './context/ThemeContext';
import useAnalytics from './hooks/useAnalytics';

const routeSeo = {
  '/': {
    title: 'AI That Answers To Enterprise Governance',
    description: 'Board-ready AI and data strategy, governed execution, and measurable value for executive teams.'
  },
  '/about': {
    title: 'About Shared Oxygen',
    description: 'Shared Oxygen helps executives turn AI and data initiatives into governed, measurable capability.'
  },
  '/services': {
    title: 'AI And Data Advisory Services',
    description: 'Data, AI, and agentic execution advisory for leaders moving from mandate to measurable value.'
  },
  '/contact': {
    title: 'Start The Conversation',
    description: 'Talk with Shared Oxygen about focused AI and data advisory support for your next executive decision.'
  },
  '/blog': {
    title: 'AI And Data Insights',
    description: 'Executive perspective on AI strategy, data instrumentation, governance, and agentic AI.'
  },
  '/eigenleads': {
    title: 'eigenLeads',
    description: 'Shared Oxygen product research for executive growth teams evaluating AI-enabled lead intelligence.'
  },
  '/pati': {
    title: 'PATi',
    description: 'Shared Oxygen product research for AI-assisted workflows, controls, and operating intelligence.'
  },
  '/privacy': {
    title: 'Privacy Policy',
    description: 'How Shared Oxygen handles information across its services, websites, and digital properties.'
  },
  '/terms': {
    title: 'Terms Of Service',
    description: 'The terms that govern use of Shared Oxygen services, content, and digital properties.'
  }
};

const getRouteSeo = (pathname) => {
  if (routeSeo[pathname]) return routeSeo[pathname];
  if (pathname.startsWith('/services/')) {
    return routeSeo['/services'];
  }
  if (pathname.startsWith('/blog/')) {
    return routeSeo['/blog'];
  }
  return routeSeo['/'];
};

const App = () => {
  const { theme } = useTheme();
  const { pathname } = useLocation();
  const seo = getRouteSeo(pathname);
  const siteOrigin = import.meta.env?.VITE_SITE_URL || 'https://sharedoxygen.com';
  const canonicalUrl = `${siteOrigin}${pathname === '/' ? '' : pathname}`;

  // Initialize analytics tracking
  useAnalytics();

  // Use data-theme attribute for easier component styling
  useEffect(() => {
    document.body.setAttribute('data-theme', theme);

    return () => document.body.removeAttribute('data-theme');
  }, [theme]);

  return (
    <div className={`flex flex-col min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
      <SEOHead
        title={seo.title}
        description={seo.description}
        image="/images/shared-oxygen-linkedin.png"
        imageAlt="AI That Answers To Enterprise Governance"
        url={canonicalUrl}
        type="website"
      />
      <ScrollToTop />
      <ContactBar />
      <Navbar />
      <main className={`flex-grow ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
};

export default App;
